/**
 * 软键盘操作辅助工具
 * - 优先通过 visualViewport 计算真实键盘高度
 * - visualViewport 不可用时，使用 resize 前后的视口差值兜底
 * - 原生壳可通过 nativeKeyboardChange 通知真实键盘高度
 * - 极端内核不触发视口变化时，Pad 端使用固定高度兜底
 * - 允许为候选栏/工具栏较高的输入法追加少量高度补偿
 */
const NATIVE_KEYBOARD_CHANGE_EVENTS = [
    'nativeKeyboardChange',
    'nativekeyboardChange',
    'nativekeyboardchange',
];
const DEFAULT_CONFIG = {
    isPad: false,
    enableDebug: false,
    minKeyboardHeight: 80,
    fallbackKeyboardHeight: 0,
    keyboardHeightOffset: 0,
    fixedKeyboardHeightOffset: 0,
};
export class SoftKeyboardHelper {
    constructor(callback, config = {}) {
        this.isListening = false;
        this.isFocused = false;
        this.activeElement = null;
        this.baseViewportHeight = 0;
        this.delayTimers = [];
        this.fixedTargetEl = null;
        this.fixedTargetOriginalStyle = null;
        this.fixedTargetOffset = 0;
        this.nativeKeyboardVisible = false;
        this.nativeKeyboardHeight = 0;
        this.handleFocusInBound = (e) => this.handleFocusIn(e);
        this.handleFocusOutBound = (e) => this.handleFocusOut(e);
        this.handleViewportChangeBound = () => this.handleViewportChange();
        this.handleNativeKeyboardChangeBound = (e) => this.handleNativeKeyboardChange(e);
        this.config = { ...DEFAULT_CONFIG, ...config };
        this.callback = callback;
        this.baseViewportHeight = this.getVisibleViewportHeight();
        this.currentStatus = {
            isShown: false,
            height: 0,
            viewportHeight: this.baseViewportHeight,
            activeElement: null,
        };
    }
    updateConfig(config = {}) {
        this.config = { ...this.config, ...config };
        if (!this.isListening)
            return;
        if (!this.config.isPad) {
            this.isFocused = false;
            this.activeElement = null;
            this.nativeKeyboardVisible = false;
            this.nativeKeyboardHeight = 0;
            this.clearDelayedRefresh();
            this.resetFixedElementOffset();
            this.updateViewportCss(this.baseViewportHeight, 0);
            this.emitStatus(false, 0, this.baseViewportHeight, null);
            return;
        }
        this.refreshStatus();
    }
    getKeyboardHeight() {
        if (this.config.fallbackKeyboardHeight > 0) {
            return this.config.fallbackKeyboardHeight;
        }
        if (!this.config.isPad) {
            return 0;
        }
        if (this.isPortrait()) {
            return 300;
        }
        return this.getLayoutViewportHeight() < 700 ? 400 : 500;
    }
    init() {
        var _a, _b;
        if (this.isListening)
            return;
        if (!this.config.isPad)
            return;
        this.updateBaseViewportHeight();
        this.updateViewportCss(this.baseViewportHeight, 0);
        document.addEventListener('focusin', this.handleFocusInBound);
        document.addEventListener('focusout', this.handleFocusOutBound);
        window.addEventListener('resize', this.handleViewportChangeBound);
        window.addEventListener('orientationchange', this.handleViewportChangeBound);
        (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.addEventListener('resize', this.handleViewportChangeBound);
        (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.addEventListener('scroll', this.handleViewportChangeBound);
        NATIVE_KEYBOARD_CHANGE_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, this.handleNativeKeyboardChangeBound);
        });
        this.isListening = true;
        this.emitStatus(false, 0, this.baseViewportHeight, null);
        this.debug('初始化完成');
    }
    handleFocusIn(e) {
        if (!this.config.isPad)
            return;
        const targetEl = e.target;
        if (!targetEl || !this.isTargetElement(targetEl))
            return;
        if (this.focusOutTimer) {
            window.clearTimeout(this.focusOutTimer);
            this.focusOutTimer = undefined;
        }
        this.isFocused = true;
        this.activeElement = targetEl;
        this.refreshStatus(false);
        this.scheduleDelayedRefresh(targetEl);
        this.debug('目标元素聚焦，监听软键盘变化', targetEl);
    }
    handleFocusOut(e) {
        if (!this.config.isPad)
            return;
        const targetEl = e.target;
        if (!targetEl || !this.isTargetElement(targetEl))
            return;
        if (this.focusOutTimer) {
            window.clearTimeout(this.focusOutTimer);
        }
        this.focusOutTimer = window.setTimeout(() => {
            const activeEl = document.activeElement;
            if (activeEl && this.isTargetElement(activeEl)) {
                this.isFocused = true;
                this.activeElement = activeEl;
                this.refreshStatus();
                return;
            }
            this.isFocused = false;
            this.activeElement = null;
            this.clearDelayedRefresh();
            this.updateBaseViewportHeight();
            this.resetFixedElementOffset();
            this.updateViewportCss(this.baseViewportHeight, 0);
            this.emitStatus(false, 0, this.baseViewportHeight, null);
            this.debug('目标元素失焦，软键盘收起');
        }, 80);
    }
    handleViewportChange() {
        if (!this.config.isPad)
            return;
        if (this.viewportTimer) {
            window.clearTimeout(this.viewportTimer);
        }
        this.viewportTimer = window.setTimeout(() => {
            this.viewportTimer = undefined;
            this.refreshStatus();
        }, 30);
    }
    handleNativeKeyboardChange(e) {
        if (!this.config.isPad)
            return;
        const detail = e.detail || {};
        const visible = this.normalizeNativeKeyboardVisible(detail.visible);
        const height = visible ? this.normalizeNativeKeyboardHeight(detail.height) : 0;
        const activeTargetEl = this.getActiveTargetElement();
        this.nativeKeyboardVisible = visible;
        this.nativeKeyboardHeight = height;
        if (!visible) {
            this.isFocused = false;
            this.activeElement = null;
            this.clearDelayedRefresh();
            this.resetFixedElementOffset();
            this.updateViewportCss(this.baseViewportHeight, 0);
            this.emitStatus(false, 0, this.baseViewportHeight, null);
            window.setTimeout(() => this.updateBaseViewportHeight(), 120);
            this.debug('原生键盘收起', detail);
            return;
        }
        if (!activeTargetEl)
            return;
        this.isFocused = true;
        this.activeElement = activeTargetEl;
        this.refreshStatus(!this.hasNativeKeyboardHeight());
        this.scheduleDelayedRefresh(activeTargetEl);
        this.debug('原生键盘弹起', detail);
    }
    refreshStatus(useFallback = true) {
        if (!this.isFocused) {
            this.updateBaseViewportHeight();
            this.resetFixedElementOffset();
            this.updateViewportCss(this.baseViewportHeight, 0);
            this.emitStatus(false, 0, this.baseViewportHeight, null);
            return;
        }
        const keyboardHeight = this.getMeasuredKeyboardHeight(useFallback);
        const minViewportHeight = this.getMinViewportHeight();
        const viewportHeight = Math.max(minViewportHeight, this.getVisibleViewportHeightFromKeyboard(keyboardHeight));
        this.updateViewportCss(viewportHeight, keyboardHeight);
        this.updateFixedElementOffset(this.activeElement, viewportHeight, keyboardHeight);
        this.emitStatus(keyboardHeight > 0, keyboardHeight, viewportHeight, this.activeElement);
    }
    scheduleDelayedRefresh(targetEl) {
        this.clearDelayedRefresh();
        [120, 320].forEach((delay) => {
            const timer = window.setTimeout(() => {
                this.delayTimers = this.delayTimers.filter((item) => item !== timer);
                this.refreshStatus();
                this.scrollToActiveElementIfNeeded(targetEl);
            }, delay);
            this.delayTimers.push(timer);
        });
    }
    clearDelayedRefresh() {
        this.delayTimers.forEach((timer) => window.clearTimeout(timer));
        this.delayTimers = [];
    }
    getMeasuredKeyboardHeight(useFallback = true) {
        if (this.hasNativeKeyboardHeight()) {
            return Math.round(this.limitKeyboardHeight(this.nativeKeyboardHeight));
        }
        const visibleHeight = this.getVisibleViewportHeight();
        const layoutHeight = this.getLayoutViewportHeight();
        const stableHeight = Math.max(this.baseViewportHeight, layoutHeight, visibleHeight);
        const visualViewportBottom = this.getVisualViewportBottom(visibleHeight);
        let keyboardHeight = Math.max(0, stableHeight - visualViewportBottom);
        if (keyboardHeight < this.config.minKeyboardHeight) {
            const resizeDiff = Math.max(0, this.baseViewportHeight - visibleHeight);
            keyboardHeight = resizeDiff >= this.config.minKeyboardHeight ? resizeDiff : 0;
        }
        if (useFallback && keyboardHeight < this.config.minKeyboardHeight) {
            keyboardHeight = this.getKeyboardHeight();
        }
        return Math.round(this.applyKeyboardHeightOffset(keyboardHeight));
    }
    getVisibleViewportHeightFromKeyboard(keyboardHeight) {
        if (this.hasNativeKeyboardHeight()) {
            return Math.round(Math.max(0, this.baseViewportHeight - keyboardHeight));
        }
        if (window.visualViewport) {
            const visualHeight = Math.round(window.visualViewport.height);
            const measuredDiff = Math.max(0, this.baseViewportHeight - this.getVisualViewportBottom(visualHeight));
            const viewportHeightFromKeyboard = Math.round(Math.max(0, this.baseViewportHeight - keyboardHeight));
            if (keyboardHeight > 0 &&
                (measuredDiff < this.config.minKeyboardHeight || viewportHeightFromKeyboard < visualHeight)) {
                return viewportHeightFromKeyboard;
            }
            return visualHeight;
        }
        return Math.round(Math.max(0, this.baseViewportHeight - keyboardHeight));
    }
    applyKeyboardHeightOffset(keyboardHeight) {
        const offset = Math.max(0, this.config.keyboardHeightOffset || 0);
        if (keyboardHeight <= 0 || offset <= 0) {
            return keyboardHeight;
        }
        return this.limitKeyboardHeight(keyboardHeight + offset);
    }
    limitKeyboardHeight(keyboardHeight) {
        const maxKeyboardHeight = Math.max(0, this.baseViewportHeight - this.getMinViewportHeight());
        return Math.min(Math.max(0, keyboardHeight), maxKeyboardHeight);
    }
    hasNativeKeyboardHeight() {
        if (this.shouldIgnoreNativeKeyboardHeightForAinoteA2Portrait()) {
            return false;
        }
        return this.nativeKeyboardVisible && this.nativeKeyboardHeight > 0;
    }
    shouldIgnoreNativeKeyboardHeightForAinoteA2Portrait() {
        return /AINOTE-A2/i.test(navigator.userAgent || '') && this.isPortrait();
    }
    getVisibleViewportHeight() {
        var _a;
        const visualHeight = ((_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.height) || 0;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        if (visualHeight && windowHeight) {
            return Math.round(Math.min(visualHeight, windowHeight));
        }
        return Math.round(visualHeight || windowHeight || 0);
    }
    getLayoutViewportHeight() {
        return Math.round(window.innerHeight ||
            document.documentElement.clientHeight ||
            this.baseViewportHeight ||
            0);
    }
    getLayoutViewportWidth() {
        return Math.round(window.innerWidth ||
            document.documentElement.clientWidth ||
            0);
    }
    isPortrait() {
        var _a, _b, _c, _d;
        const orientationType = ((_b = (_a = window.screen) === null || _a === void 0 ? void 0 : _a.orientation) === null || _b === void 0 ? void 0 : _b.type) || '';
        if (orientationType.includes('portrait')) {
            return true;
        }
        if (orientationType.includes('landscape')) {
            return false;
        }
        if (((_c = window.screen) === null || _c === void 0 ? void 0 : _c.height) && ((_d = window.screen) === null || _d === void 0 ? void 0 : _d.width)) {
            return window.screen.height >= window.screen.width;
        }
        return this.getLayoutViewportHeight() >= this.getLayoutViewportWidth();
    }
    getMinViewportHeight() {
        return this.isPortrait() ? 200 : 160;
    }
    getVisualViewportBottom(visibleHeight) {
        var _a;
        const offsetTop = Math.max(0, Math.round(((_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.offsetTop) || 0));
        return visibleHeight + offsetTop;
    }
    getViewportVisibleTop() {
        var _a;
        return Math.max(0, Math.round(((_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.offsetTop) || 0));
    }
    getViewportVisibleBottom() {
        const visualViewportBottom = window.visualViewport
            ? this.getVisualViewportBottom(Math.round(window.visualViewport.height))
            : this.getVisibleViewportHeight();
        const statusViewportBottom = this.currentStatus.viewportHeight || this.getVisibleViewportHeight();
        const keyboardOcclusionHeight = this.getKeyboardOcclusionHeight();
        const keyboardViewportBottom = keyboardOcclusionHeight > 0
            ? Math.max(0, this.baseViewportHeight - keyboardOcclusionHeight)
            : Number.POSITIVE_INFINITY;
        return Math.round(Math.min(visualViewportBottom, statusViewportBottom, keyboardViewportBottom));
    }
    getKeyboardOcclusionHeight() {
        if (this.hasNativeKeyboardHeight()) {
            return this.limitKeyboardHeight(this.nativeKeyboardHeight);
        }
        return Math.max(0, this.currentStatus.height || 0);
    }
    updateBaseViewportHeight() {
        this.baseViewportHeight = Math.max(this.getVisibleViewportHeight(), this.getLayoutViewportHeight());
    }
    updateViewportCss(height, keyboardHeight) {
        if (!height)
            return;
        document.documentElement.style.setProperty('--app-viewport-height', `${Math.round(height)}px`);
        document.documentElement.style.setProperty('--app-keyboard-height', `${Math.round(Math.max(0, keyboardHeight))}px`);
    }
    findFixedAncestor(el) {
        let currentEl = el instanceof HTMLElement ? el : (el === null || el === void 0 ? void 0 : el.parentElement) || null;
        while (currentEl && currentEl !== document.body) {
            const computedStyle = window.getComputedStyle(currentEl);
            if (computedStyle.position === 'fixed') {
                return currentEl;
            }
            currentEl = currentEl.parentElement;
        }
        return null;
    }
    updateFixedElementOffset(activeEl, viewportHeight, keyboardHeight) {
        var _a;
        if (!activeEl || keyboardHeight <= 0) {
            this.resetFixedElementOffset();
            return;
        }
        const fixedEl = this.findFixedAncestor(activeEl);
        if (!fixedEl) {
            this.resetFixedElementOffset();
            return;
        }
        if (this.fixedTargetEl !== fixedEl) {
            this.resetFixedElementOffset();
            this.fixedTargetEl = fixedEl;
            this.fixedTargetOriginalStyle = {
                transform: fixedEl.style.transform,
                computedTransform: window.getComputedStyle(fixedEl).transform,
                transition: fixedEl.style.transition,
                willChange: fixedEl.style.willChange,
            };
        }
        const activeRect = activeEl.getBoundingClientRect();
        const gap = 12;
        const fixedKeyboardHeightOffset = this.hasNativeKeyboardHeight()
            ? 0
            : Math.max(0, this.config.fixedKeyboardHeightOffset || 0);
        const fixedViewportHeight = Math.max(0, viewportHeight - fixedKeyboardHeightOffset);
        const fixedKeyboardHeight = Math.max(0, keyboardHeight + fixedKeyboardHeightOffset);
        const rawActiveBottom = activeRect.bottom + this.fixedTargetOffset;
        const nextOffset = Math.min(Math.max(0, rawActiveBottom - fixedViewportHeight + gap), Math.max(0, fixedKeyboardHeight + gap));
        if (nextOffset <= 0) {
            this.resetFixedElementOffset();
            return;
        }
        this.fixedTargetOffset = Math.round(nextOffset);
        const originalTransform = ((_a = this.fixedTargetOriginalStyle) === null || _a === void 0 ? void 0 : _a.computedTransform) || '';
        fixedEl.style.transform = `translate3d(0, -${this.fixedTargetOffset}px, 0)${originalTransform && originalTransform !== 'none' ? ` ${originalTransform}` : ''}`;
        fixedEl.style.transition = 'none';
        fixedEl.style.willChange = 'transform';
        document.documentElement.style.setProperty('--app-keyboard-fixed-offset', `${this.fixedTargetOffset}px`);
    }
    resetFixedElementOffset() {
        var _a, _b, _c;
        if (!this.fixedTargetEl) {
            document.documentElement.style.setProperty('--app-keyboard-fixed-offset', '0px');
            this.fixedTargetOffset = 0;
            return;
        }
        this.fixedTargetEl.style.transform = ((_a = this.fixedTargetOriginalStyle) === null || _a === void 0 ? void 0 : _a.transform) || '';
        this.fixedTargetEl.style.transition = ((_b = this.fixedTargetOriginalStyle) === null || _b === void 0 ? void 0 : _b.transition) || '';
        this.fixedTargetEl.style.willChange = ((_c = this.fixedTargetOriginalStyle) === null || _c === void 0 ? void 0 : _c.willChange) || '';
        this.fixedTargetEl = null;
        this.fixedTargetOriginalStyle = null;
        this.fixedTargetOffset = 0;
        document.documentElement.style.setProperty('--app-keyboard-fixed-offset', '0px');
    }
    normalizeNativeKeyboardVisible(visible) {
        return visible === true || visible === 'true' || visible === 1 || visible === '1';
    }
    normalizeNativeKeyboardHeight(height) {
        const nextHeight = Number(height);
        return Number.isFinite(nextHeight) ? Math.max(0, Math.round(nextHeight)) : 0;
    }
    getActiveTargetElement() {
        const activeEl = document.activeElement;
        return activeEl && this.isTargetElement(activeEl) ? activeEl : null;
    }
    emitStatus(isShown, height, viewportHeight, activeElement) {
        const nextStatus = {
            isShown,
            height,
            viewportHeight,
            activeElement,
        };
        const prev = this.currentStatus;
        const shouldEmit = prev.isShown !== nextStatus.isShown ||
            prev.height !== nextStatus.height ||
            prev.viewportHeight !== nextStatus.viewportHeight;
        this.currentStatus = nextStatus;
        if (shouldEmit) {
            this.callback(this.getCurrentStatus());
        }
    }
    isTargetElement(el) {
        var _a;
        if (this.isSelectLikeElement(el)) {
            return false;
        }
        const tagName = el.tagName;
        const inputEl = el;
        const textareaEl = el;
        const ignoredInputTypes = ['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'hidden', 'range', 'color'];
        const isNativeTextInput = tagName === 'INPUT' &&
            !ignoredInputTypes.includes(inputEl.type) &&
            !inputEl.readOnly &&
            !inputEl.disabled;
        const isNativeTextarea = tagName === 'TEXTAREA' &&
            !textareaEl.readOnly &&
            !textareaEl.disabled;
        const hasTextareaClass = el.classList.contains('el-textarea__inner') && isNativeTextarea;
        const hasInputClass = el.classList.contains('el-input__inner') && isNativeTextInput;
        const isNativeEditable = isNativeTextarea ||
            isNativeTextInput ||
            el.isContentEditable;
        const isEidtableId = el.id === 'eidtableId';
        let isEidtableIdNotInEditableSelectPopover = false;
        if (isEidtableId) {
            const isInEditableSelectPopoverByClosest = el.closest('.EditableSelectPopover') !== null;
            const bodyEditableSelectPopover = document.body.querySelector('.EditableSelectPopover');
            const isInEditableSelectPopoverByBody = (_a = bodyEditableSelectPopover === null || bodyEditableSelectPopover === void 0 ? void 0 : bodyEditableSelectPopover.contains(el)) !== null && _a !== void 0 ? _a : false;
            isEidtableIdNotInEditableSelectPopover =
                !isInEditableSelectPopoverByClosest && !isInEditableSelectPopoverByBody;
        }
        return hasTextareaClass || hasInputClass || isNativeEditable || isEidtableIdNotInEditableSelectPopover;
    }
    isSelectLikeElement(el) {
        return el.closest('.el-select, .el-cascader, .el-date-editor, .el-time-select, .el-time-picker, .el-color-picker, .el-tree-select') !== null;
    }
    isElementInViewport(el) {
        var _a;
        const rect = el.getBoundingClientRect();
        const scrollParent = this.findScrollParent(el);
        const visibleBounds = this.getScrollParentVisibleBounds(scrollParent);
        const viewportWidth = ((_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.width) || window.innerWidth || document.documentElement.clientWidth;
        const gap = 12;
        return (rect.top >= visibleBounds.top + gap &&
            rect.bottom <= visibleBounds.bottom - gap &&
            rect.right > 0 &&
            rect.left < viewportWidth);
    }
    findScrollParent(el) {
        const scrollbarWrap = el.closest('.el-scrollbar__wrap');
        if (this.isScrollableElement(scrollbarWrap)) {
            return scrollbarWrap;
        }
        let currentEl = el.parentElement;
        while (currentEl) {
            const computedStyle = window.getComputedStyle(currentEl);
            const overflowY = computedStyle.overflowY;
            const isScrollable = overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowY === 'overlay';
            if (isScrollable && this.isScrollableElement(currentEl)) {
                return currentEl;
            }
            if (currentEl === document.body)
                break;
            currentEl = currentEl.parentElement;
        }
        return window;
    }
    isScrollableElement(el) {
        if (!(el instanceof HTMLElement))
            return false;
        const computedStyle = window.getComputedStyle(el);
        return (el.clientHeight > 0 &&
            el.scrollHeight > el.clientHeight + 1 &&
            computedStyle.height !== '0px');
    }
    getScrollParentVisibleBounds(scrollParent) {
        const viewportTop = this.getViewportVisibleTop();
        const viewportBottom = this.getViewportVisibleBottom();
        if (scrollParent === window) {
            return {
                top: viewportTop,
                bottom: viewportBottom,
            };
        }
        const parentRect = scrollParent.getBoundingClientRect();
        return {
            top: Math.max(parentRect.top, viewportTop),
            bottom: Math.min(parentRect.bottom, viewportBottom),
        };
    }
    scrollToActiveElementIfNeeded(el) {
        if (!el || this.isElementInViewport(el))
            return;
        try {
            const scrollParent = this.findScrollParent(el);
            const visibleBounds = this.getScrollParentVisibleBounds(scrollParent);
            const gap = 16;
            const elementRect = el.getBoundingClientRect();
            if (scrollParent === window) {
                const offsetTop = elementRect.top < visibleBounds.top + gap
                    ? elementRect.top - visibleBounds.top - gap
                    : elementRect.bottom - visibleBounds.bottom + gap;
                window.scrollBy(0, offsetTop);
                return;
            }
            const parentEl = scrollParent;
            let targetScrollTop = parentEl.scrollTop;
            if (elementRect.top < visibleBounds.top + gap) {
                targetScrollTop -= visibleBounds.top + gap - elementRect.top;
            }
            else if (elementRect.bottom > visibleBounds.bottom - gap) {
                targetScrollTop += elementRect.bottom - (visibleBounds.bottom - gap);
            }
            parentEl.scrollTop = Math.max(0, targetScrollTop);
        }
        catch (error) {
            this.debug('滚动到聚焦元素失败，使用 scrollIntoView 兜底', error);
            el.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
    }
    destroy() {
        var _a, _b;
        if (!this.isListening)
            return;
        document.removeEventListener('focusin', this.handleFocusInBound);
        document.removeEventListener('focusout', this.handleFocusOutBound);
        window.removeEventListener('resize', this.handleViewportChangeBound);
        window.removeEventListener('orientationchange', this.handleViewportChangeBound);
        (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.removeEventListener('resize', this.handleViewportChangeBound);
        (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.removeEventListener('scroll', this.handleViewportChangeBound);
        NATIVE_KEYBOARD_CHANGE_EVENTS.forEach((eventName) => {
            window.removeEventListener(eventName, this.handleNativeKeyboardChangeBound);
        });
        if (this.viewportTimer)
            window.clearTimeout(this.viewportTimer);
        if (this.focusOutTimer)
            window.clearTimeout(this.focusOutTimer);
        this.clearDelayedRefresh();
        this.resetFixedElementOffset();
        this.updateViewportCss(this.baseViewportHeight, 0);
        this.isListening = false;
        this.isFocused = false;
        this.activeElement = null;
        this.nativeKeyboardVisible = false;
        this.nativeKeyboardHeight = 0;
        this.currentStatus = {
            isShown: false,
            height: 0,
            viewportHeight: this.baseViewportHeight,
            activeElement: null,
        };
        this.debug('销毁监听完成');
    }
    getCurrentStatus() {
        return { ...this.currentStatus };
    }
    debug(message, data) {
        if (!this.config.enableDebug)
            return;
        console.log(`[SoftKeyboardHelper] ${message}`, data || '');
    }
}
export const defaultSoftKeyboardHelper = new SoftKeyboardHelper((status) => {
    console.log('[SoftKeyboardHelper] 默认实例 - 软键盘状态变化：', status);
}, { enableDebug: false });
